import { useCallback, useEffect } from 'react';

export enum Message {
	ContentCreated,
	ContentUnarchived,
	SmartFolderSyncStateChanged,
	PageTreeRefreshRequested,
	ContentRemoved,
}

type MessageRegistry = {
	[Message.ContentCreated]: {
		contentId: string;
		parentId: string | undefined;
		spaceKey: string;
		type: string;
		subType: string | undefined;
	};
	[Message.ContentUnarchived]: { contentId: string };
	/**
	 * Fired when a folder is synced or unsynced (its `subType` flips between
	 * `'managed'` and `null`). Lets the page tree update the folder icon and
	 * invalidate its children so newly synced content appears without a page
	 * refresh.
	 */
	[Message.SmartFolderSyncStateChanged]: {
		contentId: string;
		subType: 'managed' | null;
	};
	/** Refetch the children of the given page in the visible page tree. */
	[Message.PageTreeRefreshRequested]: { parentId: string };
	/**
	 * Fired when content has left the tree — archived or deleted — so the page tree can drop it
	 * without waiting for a refetch. Carries the whole batch rather than one id per message: the
	 * subscriber removes them in a single pass, which a per-id message could not guarantee.
	 *
	 * Optimistic and unrevertable — the page tree discards the node's data — so publish only once
	 * the backend has confirmed the removal.
	 */
	[Message.ContentRemoved]: { contentIds: string[] };
};

class MessageBus {
	private subscribers: Record<string, Array<Function>> = {};

	publish<MessageType extends keyof MessageRegistry>(
		type: MessageType,
		data: MessageRegistry[MessageType],
	) {
		if (!this.subscribers[type]) {
			return;
		}

		this.subscribers[type].forEach((subscriber) => subscriber(data));
	}

	subscribe<MessageType extends keyof MessageRegistry>(
		type: MessageType,
		callback: (data: MessageRegistry[MessageType]) => void,
	) {
		if (!this.subscribers[type]) {
			this.subscribers[type] = [];
		}
		this.subscribers[type].push(callback);
	}

	unsubscribe<MessageType extends keyof MessageRegistry>(
		type: MessageType,
		callback: (data: MessageRegistry[MessageType]) => void,
	) {
		this.subscribers[type] = this.subscribers[type].filter((subscriber) => subscriber !== callback);
	}
}

const messageBus = new MessageBus();

export const useMessagePublisher = <MessageType extends keyof MessageRegistry>(
	type: MessageType,
): ((data: MessageRegistry[MessageType]) => void) => {
	return useCallback(
		(data: MessageRegistry[MessageType]) => {
			messageBus.publish(type, data);
		},
		[type],
	);
};

/** Non-hook publish helper for use outside React components. */
export const publishMessage = <MessageType extends keyof MessageRegistry>(
	type: MessageType,
	data: MessageRegistry[MessageType],
): void => {
	messageBus.publish(type, data);
};

export const useMessageSubscriber = <MessageType extends keyof MessageRegistry>(
	type: MessageType,
	callback: (data: MessageRegistry[MessageType]) => void,
): void => {
	useEffect(() => {
		messageBus.subscribe(type, callback);

		return () => {
			messageBus.unsubscribe(type, callback);
		};
	}, [callback, type]);
};
